import React, { Fragment, useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import Draggable from 'react-draggable';
// store
import { useStore } from 'modules/Stores';
// modules
import LocaleService from 'modules/Localization';
import { CleverTapService } from 'modules/CleverTap';
// constants
import { EVENTS } from 'modules/Analytics/consts';
// UI
import { View, Text } from '@upstox/upstox-ui';
import OptionChainHeader from './components/OptionChainHeader';
import SettingsContainer from './components/SettingsContainer';
import OptionChainTableContainer from './components/OptionChainTableContainer';
// styles
import * as css from './styles';

const OptionChainModal = () => {
  const { toggleModal } = useStore('AppStore');
  const {
    scripData,
    openSettings,
    setOptionChainData,
    clear,
    unsubscribeOptionChainScrips,
    isOptionChainAvailable,
    selectedExpiry,
    updateOptionStrikes,
    spotToken,
    isMinimised,
    setIsMinimised,
    setDragging,
    futureScrip,
  } = useStore('OptionChainStore');
  const timeout = useRef<ReturnType<typeof setTimeout> | null>();

  useEffect(() => {
    updateOptionStrikes(selectedExpiry, spotToken);
  }, [spotToken, selectedExpiry, updateOptionStrikes]);

  useEffect(() => {
    if (isOptionChainAvailable) {
      CleverTapService.sendEvent(EVENTS.OPTION_CHAIN_VIEWED, {});
    }
  }, [isOptionChainAvailable]);

  useEffect(() => () => {
    // clear timeout
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  }, []);

  useEffect(
    () => () => {
      unsubscribeOptionChainScrips();
      // clear data
      clear();
    },
    [clear, unsubscribeOptionChainScrips],
  );

  const closeModal = useCallback(() => {
    toggleModal('optionChain');
  }, [toggleModal]);

  const refreshOptionChainTable = useCallback(async () => {
    unsubscribeOptionChainScrips();
    // clear data
    clear();
    await setOptionChainData(scripData?.exchangeToken || '', true);
  }, [clear, scripData, setOptionChainData, unsubscribeOptionChainScrips]);

  const handleOustideClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      if (
        !document
          .getElementById('optionChainModal')
          ?.contains(event.target as Node)
      ) {
        setIsMinimised(true);
      }
    },
    [setIsMinimised],
  );

  return (
    <View css={css.modalContainer}>
      {isMinimised ? (
        <Draggable
          bounds="body"
          onStart={() => setDragging(true)}
          onStop={() => setDragging(false)}
        >
          <View flexDirection="column" css={css.minimizedContainer}>
            <OptionChainHeader
              closeModal={closeModal}
              refreshOptionChainTable={refreshOptionChainTable}
            />
          </View>
        </Draggable>
      ) : (
        <View css={css.maximizedContainer} onClick={handleOustideClick}>
          <View flexDirection="column" css={css.base} id="optionChainModal">
            <OptionChainHeader
              closeModal={closeModal}
              refreshOptionChainTable={refreshOptionChainTable}
            />
            <Fragment>
              {openSettings && <SettingsContainer />}
              <View css={css.optionChainTableContainer}>
                {isOptionChainAvailable ? (
                  <OptionChainTableContainer />
                ) : (
                  <View css={css.notAvailableContainer}>
                    <Text
                      variant="meta"
                      color="text.1"
                      size="l"
                      family="tvFont"
                    >
                      {LocaleService.translate(
                        'OPTION_CHAIN_NOT_AVAILABLE',
                        futureScrip?.toString() ?? '',
                      )}
                    </Text>
                  </View>
                )}
              </View>
            </Fragment>
          </View>
        </View>
      )}
    </View>
  );
};

export default observer(OptionChainModal);
